import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, fmt, downloadFile } from "@/lib/api";
import { ScoreEditDialog } from "@/components/fly/ScoreEditDialog";
import { FileDown } from "lucide-react";

const downloadPdf = async (pid, name) => {
  try {
    await downloadFile({ path: `/admin/performances/${pid}/card.pdf`,
      filename: `karta-ocen-${name.toLowerCase().replace(/[^a-z0-9ąćęłńóśźż]+/g, "-")}.pdf` });
  } catch { toast.error("Nie udało się pobrać PDF"); }
};

const signature = (s) => JSON.stringify([s.card_name, s.sections.map((x) => [x.name, x.criteria]), (s.penalty_defs || []).map((p) => p.key)]);

export const ScoreBreakdownDialog = ({ pid, onClose, onChange }) => {
  const [d, setD] = useState(null);
  const [edit, setEdit] = useState(null);
  const load = () => api.get(`/admin/performances/${pid}/scores`).then((r) => setD(r.data));

  useEffect(() => {
    if (!pid) return;
    setD(null);
    load();
  }, [pid]); // eslint-disable-line react-hooks/exhaustive-deps

  const clear = async (n) => {
    if (!window.confirm(`Usunąć ocenę sędziego ${n}? Sędzia będzie musiał ocenić ponownie.`)) return;
    await api.delete(`/admin/performances/${pid}/scores/${n}`);
    toast.success("Ocena usunięta");
    load();
    onChange?.();
  };

  const J = d?.judges || [];
  const byJudge = Object.fromEntries((d?.scores || []).filter((s) => J.includes(s.judge_number)).map((s) => [s.judge_number, s]));
  const total = Object.values(byJudge).reduce((s, x) => s + x.total, 0);
  const groups = {};
  J.filter((n) => byJudge[n]).forEach((n) => { (groups[signature(byJudge[n])] ||= []).push(n); });
  const missing = J.filter((n) => !byJudge[n]);
  return (
    <>
    <Dialog open={!!pid} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#1C2541] border-blue-500/30 w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto p-4 sm:p-6" data-testid="breakdown-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl uppercase">{d ? d.performance.name : "Ładowanie…"}</DialogTitle>
          {d && <p className="text-xs font-mono text-blue-400">{d.performance.category} · {d.performance.age_group}</p>}
          {d && <button data-testid="breakdown-pdf-btn" onClick={() => downloadPdf(pid, d.performance.name)} className="mt-2 self-start h-10 px-4 rounded-xl bg-blue-600 hover:bg-blue-500 text-sm font-semibold flex items-center gap-2"><FileDown className="w-4 h-4" />Pobierz kartę ocen (PDF)</button>}
        </DialogHeader>
        {d && (
          <>
            {Object.values(groups).map((nums) => <JudgeGroup key={nums.join("-")} nums={nums} byJudge={byJudge} onEdit={(n) => setEdit({ judge: n, score: byJudge[n] })} onClear={clear} />)}
            {missing.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="breakdown-missing">
                <span className="text-amber-300">Brak oceny:</span>
                {missing.map((n) => <button key={n} data-testid={`breakdown-edit-${n}`} onClick={() => setEdit({ judge: n, score: null })} className="h-8 px-3 rounded-lg border border-blue-500/40 text-blue-200 hover:bg-blue-500/10 text-xs">S{n} – dodaj</button>)}
              </div>
            )}
            <div data-testid="breakdown-comments">
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400 mb-2">Komentarze sędziów</div>
              {(d.scores || []).filter((s) => s.note).length === 0 && <p className="text-xs text-slate-500">Brak komentarzy.</p>}
              {(d.scores || []).filter((s) => s.note).map((s) => (
                <div key={s.judge_number} data-testid={`breakdown-comment-${s.judge_number}`} className="rounded-lg bg-[#0B132B] border border-white/5 p-3 mb-2 text-sm">
                  <div className="font-mono text-[11px] text-blue-300 mb-1">Sędzia {s.judge_number}</div>
                  <p className="text-slate-200 whitespace-pre-wrap">{s.note}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-between items-end border-t border-white/10 pt-3">
              <span className="font-mono text-xs text-slate-400">Ocen: {Object.keys(byJudge).length}/{J.length}</span>
              <span data-testid="breakdown-total" className="font-display text-4xl font-black">{fmt(total)} pkt</span>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
    {d && <ScoreEditDialog edit={edit} perf={d.performance} onClose={() => setEdit(null)} onSaved={() => { load(); onChange?.(); }} />}
    </>
  );
};

const JudgeGroup = ({ nums, byJudge, onEdit, onClear }) => {
  const first = byJudge[nums[0]];
  const span = nums.length + 1;
  return (
    <div className="overflow-x-auto" data-testid={`breakdown-group-${nums.join("-")}`}>
      <table className="w-full text-sm">
        <thead><tr className="text-left font-mono text-[10px] uppercase tracking-wider text-slate-400"><th className="py-2">Karta: {first.card_name}</th>{nums.map((n) => <th key={n} className="py-2 text-center">S{n}</th>)}</tr></thead>
        <tbody>
          {first.sections.map((sec, si) => (
            <Rows key={si} sec={sec} si={si} nums={nums} byJudge={byJudge} span={span} />
          ))}
          <Section label="Odjęcia (pkt)" span={span} />
          <Row label="Suma odjęć" vals={nums.map((n) => -byJudge[n].penalty_sum)} red />
          <tr className="border-t border-blue-500/30 font-bold"><td className="py-2">Wynik sędziego</td>{nums.map((n) => <td key={n} data-testid={`breakdown-judge-total-${n}`} className="text-center font-display text-xl">{fmt(byJudge[n].total)}</td>)}</tr>
          <tr><td />{nums.map((n) => <td key={n} className="text-center whitespace-nowrap">
            <button data-testid={`breakdown-edit-${n}`} onClick={() => onEdit(n)} className="text-[11px] text-blue-300 hover:underline mr-2">edytuj</button>
            <button data-testid={`breakdown-clear-${n}`} onClick={() => onClear(n)} className="text-[11px] text-red-400 hover:underline">usuń</button>
          </td>)}</tr>
        </tbody>
      </table>
    </div>
  );
};

const Rows = ({ sec, si, nums, byJudge, span }) => (
  <>
    <Section label={sec.name} span={span} />
    {sec.criteria.map((c, i) => <Row key={`${i}-${c}`} label={c} vals={nums.map((n) => byJudge[n].sections[si]?.values[i])} />)}
  </>
);

const Section = ({ label, span }) => <tr><td colSpan={span} className="pt-3 pb-1 font-mono text-[10px] uppercase tracking-[0.2em] text-blue-400">{label}</td></tr>;
const Row = ({ label, vals, red }) => (
  <tr className="border-b border-white/5">
    <td className="py-1.5 text-slate-300 pr-3">{label}</td>
    {vals.map((v, i) => <td key={i} className={`text-center font-mono ${red && v ? "text-red-400" : ""}`}>{v == null ? "—" : fmt(v)}</td>)}
  </tr>
);
